/**
 * Scroll Reveal - Horizon Pro
 * IntersectionObserver-based scroll reveal for sections.
 * Adds .hp-revealed class on intersection with configurable stagger.
 * Reads settings from body data attributes.
 * Respects prefers-reduced-motion.
 *
 * Badeloft fix 2026-08-26: elements taller than the viewport can never
 * reach a 0.1 intersection ratio (max ratio = viewport/element height),
 * which left long single-block pages (WP-imported content on the default
 * page template) permanently at opacity 0. Tall elements now reveal on
 * first intersection; normal elements keep the original 10% behaviour.
 */

(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const stagger = parseInt(document.body.dataset.scrollStagger || '80', 10);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const el = entry.target;

        // Tall elements can never reach the 0.1 ratio; reveal them as soon
        // as they intersect at all.
        const tallerThanViewport =
          entry.boundingClientRect.height >= window.innerHeight * 0.8;
        if (!tallerThanViewport && entry.intersectionRatio < 0.1) return;

        const parent = el.closest('.section') || el.parentElement;
        if (!parent) {
          el.classList.add('hp-revealed');
          observer.unobserve(el);
          return;
        }

        // Calculate stagger index among siblings
        const siblings = Array.from(parent.children);
        const index = siblings.indexOf(el);
        const delay = index * stagger;

        setTimeout(() => {
          el.classList.add('hp-revealed');
        }, delay);

        observer.unobserve(el);
      });
    },
    {
      threshold: [0, 0.1],
      rootMargin: '0px 0px -50px 0px',
    }
  );

  function observeElements(root) {
    root.querySelectorAll('.section > *:not(.hp-revealed)').forEach((el) => {
      observer.observe(el);
    });
  }

  // Initial observation
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observeElements(document));
  } else {
    observeElements(document);
  }

  // Watch for dynamically added sections
  const mutationObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.classList && node.classList.contains('section')) {
          Array.from(node.children).forEach((child) => observer.observe(child));
        }
        if (node.querySelectorAll) {
          observeElements(node);
        }
      }
    }
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });
})();
